import { baseUrl } from "./baseUrl";
import { getUser, removeTokens, removeUser, saveUser, User } from "./auth";
import { removeConducteurs, removeContrat, removeVehicule } from "../pages/CreateReleve/api";

export async function fetchAuth(
  url: RequestInfo | URL,
  options: RequestInit = {},
  refresh: boolean = false,
  throws: boolean = true,
): Promise<Response> {
  if (!!getUser() && !refresh && new Date(getUser().expireAt) < new Date()) {
    return refreshAccessToken().then(() => fetchAuth(url, options, false, false));
  }

  const token = refresh ? getRefreshToken() : getAccessToken();
  if (!token) throw new Error("Access token not available");
  const { headers = {}, ...rest } = options;
  let response = await fetch(url, {
    //credentials: "include",
    headers: {
      Authorization: `Bearer ${token}`,
      ...headers,
    },
    ...rest,
  });

  if (response.status === 401) {
    removeUser();
    removeTokens();
    removeContrat();
    removeVehicule();
    removeConducteurs();
    window.location.href = "/login";
  }

  if (!response.ok && throws) throw await response.json();
  return response;
}

export async function fetchFile<T = any>(
  url: RequestInfo | URL,
  options: RequestInit = {},
): Promise<T> {
    return (await fetchAuth(url, { method: "POST", ...options })).json()
}
export async function fetchFilePut<T = any>(
  url: RequestInfo | URL,
  options: RequestInit = {},
): Promise<T> {
    return (await fetchAuth(url, { method: "PUT", ...options })).json()
}

function dateToLocalReplacer(key: string, value: any): any {
  if (value === null) return undefined;
  if (value instanceof Date) {
    value.setHours(2); // GMT+2 = FR (heure d'été) / GMT+1 = FR (heure d'hiver)
    return value;
  } else if (Array.isArray(value)) {
    return value.map((v) => dateToLocalReplacer(key, v));
  } else if (typeof value === "object") {
    const newObj = {} as any;
    for (const prop in value) {
      newObj[prop] = dateToLocalReplacer(prop, value[prop]);
    }
    return newObj;
  }
  return value;
}

export async function fetchJSON<T>(
  url: RequestInfo | URL,
  options: Omit<RequestInit, "body"> & {
    data?: any;
    responseType?: "json" | "blob" | "arrayBuffer" | "response";
  } = {},
): Promise<T> {
  const { data, headers, responseType = "json", ...rest } = options;
  const body = JSON.stringify(data, dateToLocalReplacer);

  const res = await fetchAuth(url, {
    headers: {
      ...headers,
      "Content-Type": "application/json",
    },
    ...rest,
    body,
  });
  if (responseType === "blob") return (await res.blob()) as T;
  if (responseType === "arrayBuffer") return (await res.arrayBuffer()) as T;
  if (responseType === "response") return res as T;
  return res.json();
}

export async function fetchAuthArrayBuffer(
  url: RequestInfo | URL,
  options: RequestInit = {},
  refresh: boolean = false,
): Promise<ArrayBuffer> {
  if (!!getUser() && !refresh && new Date(getUser().expireAt) < new Date()) {
    await refreshAccessToken();
    return fetchAuthArrayBuffer(url, options, false);
  }

  const token = refresh ? getRefreshToken() : getAccessToken();
  if (!token) throw new Error("Access token not available");

  const { headers = {}, ...rest } = options;

  const response = await fetch(url, {
    headers: {
      Authorization: `Bearer ${token}`,
      ...headers,
    },
    ...rest,
  });

  if (response.status === 401) {
    removeUser();
    removeTokens();
    removeContrat();
    removeVehicule();
    removeConducteurs();
    window.location.href = "/login";
    throw new Error("Unauthorized");
  }

  if (!response.ok) {
    try {
      throw await response.json();
    } catch {
      throw new Error(`HTTP ${response.status}`);
    }
  }

  return response.arrayBuffer();
}

export async function downloadFile(url: RequestInfo | URL, filename?: string) {
  try {
    const response = await fetchJSON<Response>(url, {
      responseType: "response",
    });

    if (!response.ok) return console.error("request failed");

    const blob = await response.blob();

    if (!filename) {
      const contentDisposition = response.headers.get("Content-Disposition");
      const filenameMatch = contentDisposition?.match(/filename=([^;]+)/);
      filename = filenameMatch?.[1]?.trim() ?? "document.pdf";
    }
    // Créer un lien avec le blob
    const link = document.createElement("a");
    link.href = window.URL.createObjectURL(blob);
    link.download = filename;
    // Ajouter le lien à la page
    document.body.appendChild(link);
    // Déclencher le téléchargement
    link.click();
    // Nettoyer
    document.body.removeChild(link);
  } catch (e) {
    console.error("Erreur lors du téléchargement du fichier", e);
  }
}

export function getAccessToken() {
  return localStorage.getItem("ACCESS_TOKEN");
}

export function getRefreshToken() {
  return localStorage.getItem("REFRESH_TOKEN");
}


export async function fetchUser() : Promise<User> {
    //alert("refreshAccessToken");
    const url = new URL("/backend/v1/user", baseUrl);
    const res = await fetchAuth(url, undefined, true, false)
    return res.json();
}

export async function refreshAccessToken() {
  return fetchUser().then((user: User) => saveUser(user));
}
